/* eslint-disable import/order */
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { getResolutionPlanSSRResource } from '@jira/router-resources__service-desk-ai-context-panel/src/ResolutionPlanSSRResource.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { queuesNavigationItems } from '@jira/router-resources__service-desk-queues/src/queuesNavigationItems.tsx';
import { issueNavigatorProjectServicedeskRoute } from '@jira/router-routes__issue-navigator-routes/src/issueNavigatorProjectServicedeskRoute.tsx';
import { serviceProjectLayout } from '@jira/platform__layouts-service-project-layout/src/index.tsx';
import { ProjectIssueNavigatorSkeleton } from '@jira/platform__skeletons/src/ui/issue-navigator/components/project-app.tsx';
import { NIN_NEW_ISSUE_NAVIGATOR } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { staticSidebarShortcutsResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import { jsmProjectMenuContentSidebarResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/jsm-customize-sidebar/index.tsx';
import { getServiceDeskSharedNavigationSingleResources } from '@jira/router-resources__service-desk-common/src/getServiceDeskSettingsResources.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { getCommonProjectResources } from './common/getCommonProjectResources.tsx';
import { getRouteReporting } from './common/getRouteReporting.tsx';
import { getSsrCircuitBreaker } from './common/getSsrCircuitBreaker.tsx';
import { adjustSsrFetchTimeout } from './common/getSsrFetchTimeout.tsx';
import {
	issueNavigatorWithoutHorizontalNavPageEntryPoint,
	issueNavigatorWithoutHorizontalNavPageEntryPointWithoutPreviewPanel,
} from './ui/index.tsx';
import { ListRedirect } from './ui/list-redirect/ListRedirect.tsx';
import type { Route } from '@atlassian/react-resource-router';

/**
 * Servicedesk Project Issue Navigator Route Entry
 * Handles JSM (Service management) projects, also known JSD (Servicedesk) projects.
 */
export const issueNavigatorProjectServicedeskRouteEntry: Route = createEntry(
	issueNavigatorProjectServicedeskRoute,
	() => ({
		group: 'issue-navigator',
		meta: {
			capability: NIN_NEW_ISSUE_NAVIGATOR,
			reporting: getRouteReporting(),
		},
		skeleton: ProjectIssueNavigatorSkeleton,
		navigation: {
			menuId: MenuIdProjects,
		},
		resources: [
			...getCommonProjectResources(),
			queuesNavigationItems,
			getConsolidationStateResource(),
			...getResolutionPlanSSRResource(),
			jsmProjectMenuContentSidebarResource,
			resourceWithCondition(
				() => fg('jsm_nin_prefetch_sidebar_resources'),
				staticSidebarShortcutsResource,
			),
			...getServiceDeskSharedNavigationSingleResources().map((resource) =>
				resourceWithCondition(() => fg('jsm_nin_prefetch_sidebar_resources'), resource),
			),
		],
		layout: serviceProjectLayout,
		bifrost: {
			bifrostGroup: 'issue-navigator',
			manifestName: 'jira-spa-issue-navigator.json',
			circuitBreakerConfig: getSsrCircuitBreaker(),
			// Experiment to test the impact of increasing the resource timeout for issue navigator routes with more performance SSR
			fetchTimeout: adjustSsrFetchTimeout(expVal('nin_ssr_resource_timeout', 'timeout', 0)),
		},

		// eslint-disable-next-line no-restricted-syntax
		component: fg('jira_list_nin_merge') ? ListRedirect : undefined,
		entryPoint: () => {
			if (fg('jira_list_nin_merge')) {
				return undefined;
			}
			return fg('nin_glance_panel_technical_changes')
				? issueNavigatorWithoutHorizontalNavPageEntryPoint
				: issueNavigatorWithoutHorizontalNavPageEntryPointWithoutPreviewPanel;
		},
		supportsPreviewPanel: true,
		isHydrateRootEnabled: true,
		excludeFromWrmDeferredLoading: true,
	}),
);
